import dynamic from 'next/dynamic'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SUPPORTED_CHAINS } from '../../constants/graphql'
import { classNames } from '../../functions'
import { useIsMounted } from '../../hooks'
import { useActivateChart } from '../../state/chart/hooks'
import { ChevronRightIcon, MinimizeIcon, WarningIconRound } from '../Icons'
import { Loader } from '../Loader'
import { Resizable } from '../Resizable'
import { ChartProps } from './Chart'

const ChartComponent = dynamic(() => import('./Chart'), {
  ssr: false,
  // loading: () => <div className="min-w-full min-h-full">Loading...</p>,
})

const Chart: React.FC<ChartProps> = ({ token, chainId }) => {
  const { t } = useTranslation()
  const isMounted = useIsMounted()

  const [resizable, setResizable] = useState<Resizable | null>(null)
  const [height, setHeight] = useState<string | number | undefined>(undefined)
  const [width, setWidth] = useState<string | number | undefined>(undefined)
  const [color, setColor] = useState<boolean>(false)
  const [active, chartSize,, { chartActive, updateChartSize }] = useActivateChart()

  const onResizeStop = useCallback(() => {
    setColor(!color)
    if (resizable)
    updateChartSize({
      width: Number(resizable.state?.width),
      height: Number(resizable.state?.height),
    })
  }, [color, resizable, updateChartSize])

  const onResize = useCallback(() => {
    if (resizable) {
      setWidth(resizable.state?.width)
      setHeight(resizable.state?.height)
    }
  }, [resizable])

  useEffect(() => {
    if (resizable) {
      if (chartSize.height !== 0 && chartSize.width !== 0) {
        resizable.setState({ height: chartSize.height, width: chartSize.width })
        setWidth(chartSize.width)
        setHeight(chartSize.height)
      } else {
        updateChartSize({ width: resizable.size.width, height: resizable.size.height })
      }
    }
  }, [chartSize, resizable, updateChartSize])

  return useMemo(() => {
    if (!isMounted) return <></>
    if (active)
      return (
        <Resizable
          className={classNames(
            chartSize.width !== 0 ? `w-[${chartSize.width}px]` : 'w-[50%]',
            chartSize.height !== 0 ? `h-[${chartSize.height}px]` : 'h-[300px]',
            'p-0 animate-movein rounded-xl mx-auto! flex-[0_0_auto]  bg-gray-200/20 flex-wrap min-h-[300px] min-w-full select-none border-2 border-transparent flex-shrink-0 !max-w-[calc(100vw-80px)] !max-h-[calc(100vh-40px)]'
          )}
          ref={setResizable}
          minHeight={330}
          minWidth={327}
          enable={{
            right: true,
            bottom: true,
            bottomRight: true,
          }}
          onResize={onResize}
          onResizeStart={onResizeStop}
          onResizeStop={onResizeStop}
        >
          <MinimizeIcon
            width={24}
            height={24}
            className="z-[100] relative flex m-1.5 float-right cursor-pointer text-slate-400 hover:text-slate-600"
            onClick={() => chartActive(false)}
          />
          <ChevronRightIcon
            className={classNames(
              'rotate-45 p-0 absolute -bottom-[3.5px] -right-[3.5px] ',
              color ? 'fill-slate-400/40 ' : 'fill-slate-700/40'
            )}
            width={24}
            height={24}
          />
          {SUPPORTED_CHAINS.includes(chainId) ? (
            <ChartComponent
              height={Number(height)}
              width={Number(width)}
              t={t}
              token={token}
              chainId={chainId}
            />
          ) : (
            <div className="-mt-0.5 absolute flex flex-col -mr-0.5 inset-0 shadow-slate-900 text-xxs items-center justify-center font-medium overflow-hidden shadow text-red-800 rounded-xl p-0 m-0 transform-gpu z-[3] pointer-events-none w-[calc(100%+0.25rem)] h-[calc(100%+0.25rem)]">
              <span className="flex z-[2] w-full h-full rounded-xl  items-center justify-center pb-8">
                <WarningIconRound width={40} height={40} />
                {t(`Charts are not supported for this chain yet`)}
                <Loader.Dots />
              </span>
            </div>
          )}
        </Resizable>
      )
    return <></>
  }, [
    isMounted, 
    active,
    chartSize,
    onResize,
    onResizeStop,
    color,
    chainId,
    height,
    width,
    t,
    token,
    chartActive,
  ])
}
export default Chart
